import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useSetRecoilState, useRecoilState } from 'recoil';
import { contentOperations } from '../models/content/index.js';
import {
  contentFeedFamily,
  setContentFeedItems,
} from '../models/feed/recoil-state';
import FeedService, { FeedServiceResponse } from '../services/feed';
import { Content } from '../models/content/types';
import { ContentFeedFamilyState } from '../models/feed/types';
import { Feature } from '../models/features/features';
import useFeatureFlag from './use-feature-flag';
import { useFeatureFlagsSettled } from './use-feature-flags-settled';

interface ContentFeedOptions {
  query?: string;
  filter?: string;
  channelId?: string;
  advocateId?: string;
  advocateFilter?: string;
  perPage?: number;
  manualFetch?: boolean;
  includeTypes?: string[];
  initiativeId?: string;
  pinned?: boolean;
}

// Prefer the authoritative total_pages from the API. Fall back to a "did this page
// come back full?" heuristic only when the backend omits total_pages — that fallback
// can give a false negative (FE-31005) when post-query visibility filtering trims a
// non-final page below pageSize.
export function computeCanLoadMore({
  curPage,
  itemsCount,
  pageSize,
  totalPages,
}: {
  curPage: number;
  itemsCount: number;
  pageSize: number;
  totalPages: number | undefined;
}): boolean {
  // Explicit "is this a usable count?" check — guards against 0 (used as
  // "missing" sentinel by older response paths), NaN, and negative values.
  if (typeof totalPages === 'number' && totalPages > 0) {
    return curPage < totalPages;
  }
  return itemsCount >= pageSize;
}

function useContentFeed({
  query,
  filter,
  channelId,
  advocateId,
  advocateFilter,
  perPage = 20,
  manualFetch,
  includeTypes,
  initiativeId,
  pinned,
}: ContentFeedOptions = {}) {
  const dispatch = useDispatch();

  //will cache the feed state in global state with this key. If we ever render 2 feeds that have the same key, they will be displayed with the same contents.
  const feedCacheKey = [
    query,
    filter,
    channelId,
    advocateId,
    advocateFilter,
    perPage,
    manualFetch,
    includeTypes,
    initiativeId,
    pinned,
  ].join('|');

  const [
    { feedItems, page, numPages, pageSize, total, canLoadMore, loaded },
    setFeed,
  ] = useRecoilState<ContentFeedFamilyState>(contentFeedFamily(feedCacheKey));

  const setFeedItems = useSetRecoilState(setContentFeedItems(feedCacheKey));

  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<unknown>();

  const addContentsToStore = useCallback(
    (contents: Content[]) => dispatch(contentOperations.addContents(contents)),
    [dispatch]
  );

  const tagUserEnabled = useFeatureFlag(Feature.TAG_USER_ENABLED);
  const advancedSearchEnabled = useFeatureFlag(Feature.IMPROVED_SEARCH);
  // FE-38038: the fetch below must not race these flags — see the effect at the end.
  const flagsSettled = useFeatureFlagsSettled();

  const feedService = useMemo(
    () =>
      new FeedService({
        query,
        filter,
        channelId,
        advocateId,
        advocateFilter,
        includeTypes,
        initiativeId,
        mention_format: tagUserEnabled ? 'with_metadata' : undefined,
        useLegacyMetadata: !advancedSearchEnabled,
      }),
    [
      advocateFilter,
      advocateId,
      channelId,
      filter,
      includeTypes,
      initiativeId,
      query,
      tagUserEnabled,
      advancedSearchEnabled,
    ]
  );

  const handleFetchFeedSuccess = useCallback(
    (res: FeedServiceResponse) => {
      const items = (res.feed || res.contents)
        .map((fi) => fi.attributes)
        .filter((fi) =>
          Object.prototype.hasOwnProperty.call(fi, 'content_type')
        );

      const [curPage, pageSize] =
        res.meta && res.meta.page
          ? [res.meta.page.number, res.meta.page.size]
          : [1, 0];

      // Capture the count the backend actually returned before our defensive
      // splice trims it to perPage. The length-fallback in computeCanLoadMore
      // needs to compare against pageSize (also from the backend) so the two
      // sides of the comparison come from the same source.
      const returnedItemsCount = items.length;
      if (perPage && items.length > perPage) {
        items.splice(perPage);
      }

      addContentsToStore(items);

      setFeed((prev) => {
        const nextTotal = res.meta.total ?? prev.total;
        const nextNumPages = res.meta.total_pages ?? prev.numPages;

        return {
          feedItems: curPage === 1 ? items : [...prev.feedItems, ...items],
          page: curPage,
          canLoadMore: computeCanLoadMore({
            curPage,
            itemsCount: returnedItemsCount,
            pageSize,
            totalPages: nextNumPages,
          }),
          total: nextTotal,
          numPages: nextNumPages,
          pageSize: res.meta.page.size,
          loaded: true,
        };
      });
      setFetching(false);
    },
    [addContentsToStore, perPage, setFeed]
  );

  const handleFetchFeedError = (err: unknown) => {
    console.error(err);

    setError(err);
  };

  const fetchFeed = useCallback(
    (resetPage = false) => {
      setFetching(true);

      if (resetPage) setFeedItems([]);

      const curPage = resetPage || !page ? undefined : page + 1;

      feedService
        .fetch({ page: curPage, perPage, pinned })
        .then(handleFetchFeedSuccess)
        .catch(handleFetchFeedError);
    },
    [feedService, handleFetchFeedSuccess, page, perPage, pinned, setFeedItems]
  );

  // FE-38038: wait for the feature flags before the first fetch. `useFeatureFlag` returns
  // `undefined` both while flags are loading and when a flag is off, so firing immediately
  // built the FeedService with `mention_format: undefined` and stored the lossy `@Name`
  // form in Redux — which is what the feed card renders (it reads title/summary from the
  // store via contentSelectors). This effect only fetches while `!loaded`, so once that
  // first flat response landed nothing ever refetched and the mention stayed unlinkable
  // for the life of the page. `flagsSettled` flips false -> true exactly once, and is true
  // on a failed flags request too, so this can delay the fetch but never block it.
  useEffect(() => {
    if (!manualFetch && !loaded && flagsSettled) {
      fetchFeed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedCacheKey, manualFetch, flagsSettled]);

  //DO NOT USE THE CONTENT IN `feedItems` DIRECTLY, USE THE CONTENT IN THE STORE BY REFERNCING THE ID OF THE CONTENT IN `feedItems`. (This is because the content in here is not updated when the content in the store is updated.)
  //This mostly just applies to the title for translations, but its a secret time bomb just waiting to blow. Theres a lot of places in the repo where this rule is broken, but please do not add any more debt onto the pile.
  return {
    fetching: !loaded || fetching,
    loaded,
    feedItems,
    page,
    numPages,
    pageSize,
    canLoadMore,
    error,
    total,
    fetchFeed,
  };
}

/**
 * @deprecated This does not use react-query, use useContentFeedQuery instead
 */
export default useContentFeed;
